/**
 * useBookingCardState Hook
 *
 * Encapsulates all calculated state for BookingCard component.
 * Extracted to reduce complexity and improve testability.
 */

import { useMemo } from "react";
import {
  calculateAbsenceFill,
  calculateAbsenceHours,
  calculateTotalHours,
  calculateHardBookingHours,
  calculateSoftBookingHours,
  isSoftBookingOnly,
  getBookingStatus,
} from "@/utils/booking";
import {
  formatBookingHours,
  formatDateTooltip,
  formatShortDate,
  isRedDay,
} from "@/utils/date";
import type { IBooking } from "@/services/types";
import { BookingType } from "@/services/types";
import { isToday, isWeekend } from "date-fns";
import type { BookingCardData, BookingStatus } from "./types";
import type { BookingItemData } from "@/components/schedule/WeekDayCell";

/**
 * Return type for useBookingCardState hook
 */
export interface BookingCardState {
  /** Calculated hours breakdown */
  hours: {
    totalHours: number;
    excludedHours: number;
    absenceHours: number;
    hardBookingHours: number;
    softBookingHours: number;
    isSoftOnly: boolean;
  };
  /** Booking status for color coding */
  status: BookingStatus;
  /** Absence fill ratio (0-1) for gradient indicator */
  absenceFill: number;
  /** Date checks */
  cardIsToday: boolean;
  cardIsWeekend: boolean;
  cardIsRedDay: boolean;
  /** Formatted date string */
  dateString: string;
  /** Tooltip content for date */
  dateTooltipContent: string;
  /** Transformed booking items for WeekDayCell */
  bookingItems: BookingItemData[];
}

/**
 * Props for useBookingCardState hook
 */
export interface UseBookingCardStateProps {
  card: BookingCardData;
  dayHours: number;
}

/**
 * Get booking type for BookingItem
 */
const getBookingItemType = (
  booking: IBooking,
): "project" | "absence" | "softBooking" => {
  if (booking.b_type === BookingType.ABSENCE) return "absence";
  if (booking.b_type === BookingType.SOFT_BOOKING) return "softBooking";
  return "project";
};

/**
 * Get project representation string for a booking
 */
const getProjectRepresentation = (booking: IBooking): string => {
  if (booking.b_type === BookingType.ABSENCE) {
    return "Fravær";
  }
  if (booking.b_type === BookingType.SOFT_BOOKING && booking.customer) {
    return `Kunde: ${booking.customer.name}`;
  }
  if (!booking.project) {
    return "Ukjent prosjekt";
  }
  const code = booking.project.code ?? "???";
  return `${code} - ${booking.project.name}`;
};

/**
 * Hook that encapsulates all calculated state for BookingCard
 */
export function useBookingCardState({
  card,
  dayHours,
}: UseBookingCardStateProps): BookingCardState {
  // Calculate booking hours
  const hours = useMemo(() => {
    const totalHours = calculateTotalHours(card.bookings);
    const excludedHours = calculateTotalHours(card.excludedBookings);
    const absenceHours = calculateAbsenceHours(card.bookings);
    const hardBookingHours = calculateHardBookingHours(card.bookings);
    const softBookingHours = calculateSoftBookingHours(card.bookings);
    const isSoftOnly = isSoftBookingOnly(card.bookings);

    return {
      totalHours: totalHours + excludedHours,
      excludedHours,
      absenceHours,
      hardBookingHours,
      softBookingHours,
      isSoftOnly,
    };
  }, [card.bookings, card.excludedBookings]);

  // Determine booking status for color coding
  // Note: The component decides whether to use this based on 'colored' prop
  const status = useMemo((): BookingStatus => {
    if (hours.isSoftOnly) {
      return hours.softBookingHours > 0 ? "fullyBooked" : "free";
    }
    return getBookingStatus(
      hours.hardBookingHours,
      hours.absenceHours,
      dayHours,
    );
  }, [hours, dayHours]);

  // Calculate absence fill for gradient indicator
  const absenceFill = useMemo(() => {
    return calculateAbsenceFill(hours.absenceHours, dayHours);
  }, [hours.absenceHours, dayHours]);

  // Date checks
  const cardIsToday = useMemo(() => isToday(card.date), [card.date]);
  const cardIsWeekend = useMemo(() => isWeekend(card.date), [card.date]);
  const cardIsRedDay = useMemo(() => isRedDay(card.date), [card.date]);

  // Format date string
  const dateString = useMemo(() => {
    return formatShortDate(card.date);
  }, [card.date]);

  // Build tooltip content for date label
  const dateTooltipContent = useMemo(() => {
    return formatDateTooltip(card.date);
  }, [card.date]);

  // Transform bookings to BookingItemData format for WeekDayCell
  const bookingItems = useMemo((): BookingItemData[] => {
    return card.bookings.map((booking) => ({
      id: booking.id,
      heading: formatBookingHours(booking.hours),
      body: getProjectRepresentation(booking),
      type: getBookingItemType(booking),
    }));
  }, [card.bookings]);

  return {
    hours,
    status,
    absenceFill,
    cardIsToday,
    cardIsWeekend,
    cardIsRedDay,
    dateString,
    dateTooltipContent,
    bookingItems,
  };
}
